import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Shell } from "@/components/Shell";
import { createGame } from "@/lib/api";

export default function HostNew() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: "",
    case_chief_complaint: "",
    case_history: "",
    case_vitals: "",
    case_physical_exam: "",
    correct_diagnosis: "",
    starting_budget: 10000,
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Give the case a title");
      return;
    }
    setLoading(true);
    try {
      const game = await createGame({
        ...form,
        starting_budget: Number(form.starting_budget) || 10000,
      });
      localStorage.setItem(`host_${game.id}`, game.host_token);
      toast.success(`Game ${game.game_code} created`);
      nav(`/host/game/${game.id}?t=${game.host_token}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create game");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell>
      <div className="max-w-3xl">
        <div className="font-mono-data text-[10px] tracking-[0.3em] text-[color:var(--dxc-volt)] uppercase">
          Host / New Session
        </div>
        <h1 className="font-serif-display text-5xl mt-2">Build the case.</h1>
        <p className="text-white/60 mt-3">
          Compose a single patient. Teams will read this before opening the catalog.
        </p>

        <form onSubmit={submit} className="mt-10 dxc-card p-8" data-testid="host-new-form">
          <Field label="Session Title" testId="game-title-input">
            <input
              data-testid="game-title-input"
              className="dxc-input"
              placeholder="e.g. Emergency Department — Chest Pain Round"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
            />
          </Field>

          <Field label="Chief Complaint" testId="cc-input">
            <input
              data-testid="cc-input"
              className="dxc-input"
              placeholder="55 y/o male with acute substernal chest pain"
              value={form.case_chief_complaint}
              onChange={(e) => update("case_chief_complaint", e.target.value)}
            />
          </Field>

          <Field label="History (HPI + PMH)" testId="history-input">
            <textarea
              data-testid="history-input"
              className="dxc-input min-h-[120px] font-serif-display text-base"
              placeholder="Onset, duration, associated symptoms, past medical history…"
              value={form.case_history}
              onChange={(e) => update("case_history", e.target.value)}
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Vitals" testId="vitals-input">
              <textarea
                data-testid="vitals-input"
                className="dxc-input min-h-[100px] font-mono-data text-sm"
                placeholder="BP 148/92, HR 108, RR 22, T 37.4, SpO2 94% RA"
                value={form.case_vitals}
                onChange={(e) => update("case_vitals", e.target.value)}
              />
            </Field>
            <Field label="Physical Exam" testId="exam-input">
              <textarea
                data-testid="exam-input"
                className="dxc-input min-h-[100px]"
                placeholder="Diaphoretic, no JVD, chest clear, cor RRR, abdomen benign…"
                value={form.case_physical_exam}
                onChange={(e) => update("case_physical_exam", e.target.value)}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Correct Diagnosis (private)" testId="correct-dx-input">
              <input
                data-testid="correct-dx-input"
                className="dxc-input"
                placeholder="e.g. Acute inferior STEMI"
                value={form.correct_diagnosis}
                onChange={(e) => update("correct_diagnosis", e.target.value)}
              />
            </Field>
            <Field label="Starting Budget ($)" testId="budget-input">
              <input
                type="number"
                data-testid="budget-input"
                className="dxc-input font-mono-data"
                min="1000"
                step="500"
                value={form.starting_budget}
                onChange={(e) => update("starting_budget", e.target.value)}
              />
            </Field>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="create-game-button"
            className="dxc-btn dxc-btn-volt mt-4 text-sm"
          >
            {loading ? "Creating…" : "Deploy Session →"}
          </button>
        </form>
      </div>
    </Shell>
  );
}

const Field = ({ label, children }) => (
  <div className="mb-6">
    <label className="font-mono-data text-[10px] tracking-[0.25em] text-white/60 uppercase">
      {label}
    </label>
    <div className="mt-2">{children}</div>
  </div>
);
